import { ColorX, LocaleX, LogicX } from "IZOX/STATIC";
import _ from "lodash";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import XProtect from "./_gears/XProtect";

/**
 * @typedef {import("./XText").fontFamily} fontFamily
 * 
 * @typedef {"on" | "off" | "none" | "name" | "honorific-prefix" | "given-name"
 * | "additional-name" | "family-name" | "honorific-suffix" | "nickname"
 * | "email" | "username" | "new-password" | "current-password"
 * | "organization-title" | "organization" | "street-address"
 * | "shipping" | "shipping" | "address-line1" | "address-line2" | "address-line3"
 * | "address-level4" | "address-level3" | "address-level2" | "address-level1"
 * | "country" | "country-name" | "postal-code" | "cc-name" | "cc-given-name"
 * | "cc-additional-name" | "cc-family-name" | "cc-number" | "cc-exp" | "cc-exp-month"
 * | "cc-exp-year" | "cc-csc" | "cc-type" | "transaction-currency"
 * | "transaction-amount" | "language" | "bday" | "bday-day" | "bday-month" | "bday-year"
 * | "sex" | "tel" | "tel-country-code" | "tel-national" | "tel-area-code"
 * | "tel-local" | "tel-extension" | "impp" | "url" | "photo" | "webauthn"} inputautocomplete
 * 
 * @typedef {Object} xinputstyleparams
 * @property {Number | String} width
 * @property {Number | String} height
 * @property {Number | String} minWidth
 * @property {Number | String} maxWidth
 * @property {Number | String} minHeight
 * @property {Number | String} maxHeight
 * @property {Number | String} lockWidth
 * @property {Number | String} lockHeight
 * @property {Number} p
 * @property {Number} pH
 * @property {Number} pV
 * @property {Number} pT
 * @property {Number} pB
 * @property {Number} pL
 * @property {Number} pR
 * @property {Number} m
 * @property {Number} mH
 * @property {Number} mV
 * @property {Number} mT
 * @property {Number} mB
 * @property {Number} mL
 * @property {Number} mR
 * @property {Number} bW
 * @property {"none" | "dotted" | "inset" | "solid" | "dashed" 
 *  | "groove" | "hidden" | "double" | "ridge" | "outset"} bStyle
 * @property {Number | [Number, Number, Number, Number]} round
 * @property {String} color
 * @property {String} bColor
 * @property {String} bgColor
 * @property {Number} focus_bW
 * @property {"none" | "dotted" | "inset" | "solid" | "dashed" 
 *  | "groove" | "hidden" | "double" | "ridge" | "outset"} focus_bStyle
* @property {String} focus_color
* @property {String} focus_bColor
* @property {String} focus_bgColor
 * @property {Number} disabled_bW
 * @property {"none" | "dotted" | "inset" | "solid" | "dashed" 
 *  | "groove" | "hidden" | "double" | "ridge" | "outset"} disabled_bStyle
 * @property {String} disabled_color
 * @property {String} disabled_bColor
 * @property {String} disabled_bgColor
 * @property {Number} error_bW
 * @property {"none" | "dotted" | "inset" | "solid" | "dashed" 
 *  | "groove" | "hidden" | "double" | "ridge" | "outset"} error_bStyle
 * @property {String} error_color
 * @property {String} error_bColor
 * @property {String} error_bgColor
 * @property {String} placeholderColor
 * @property {Boolean} shadow
 * @property {Number} shadowSize
 * @property {String} shadowColor
 * @property {Boolean} fullWidth
 * @property {Boolean} fullHeight
 * @property {Boolean} fitWidthToContent
 * @property {Boolean} fitHeightToContent
 * @property {Boolean} autoWidth
 * @property {Boolean} autoHeight
 * @property {any} forwardRef
 * 
 * @typedef {Object} xinputparams
 * @property {CSSProperties} style
 * @property {String | Number} value
 * @property {(any) => void} onChange
 * @property {(text) => void} onChangeText
 * @property {import("IZOX/__Typedef/common").lang} placeholder
 * @property {"number" | "text" | "password" | "file"} type
 * @property {Number} numberOfLines
 * @property {(value: String, e: import("react").KeyboardEventHandler) => void} onKeyDown
 * @property {(value: String, key: String, e: import("react").KeyboardEventHandler)} onArrowKeyDown
 * @property {(value: String, e: import("react").KeyboardEventHandler) => void} onEnterKeyDown
 * @property {(value: String, e: import("react").KeyboardEventHandler) => void} onDeleteKeyDown
 * @property {(value: String, e: import("react").KeyboardEventHandler) => void} onPaste
 * @property {Boolean} readOnly
 * @property {Boolean} error
 * @property {"[" | "|" | "]"} textAlign
 * @property {Number} fontSize
 * @property {fontFamily} fontFamily
 * @property {() => void} onFocus
 * @property {() => void} onBlur
 * @property {Boolean} resizable
 * @property {Boolean} multilines
 * @property {Boolean} noSpinners
 * @property {String} accept
 * @property {Boolean} jumpCursor
 * @property {Boolean} isControlled
 * @property {inputautocomplete} autoComplete
 * @property {Boolean} autoSetCursor
 * @property {Number} min
 * @property {Number} max
 * @property {Number} step
 * @property {Number} presicion
 * @property {Boolean} multiple
 * @property {Number} maxLength
 * @property {Boolean} noSpecialCharacter
 * @property {Boolean} numberOnly
 * @property {Boolean} emailOnly
 * @property {Boolean} noSpace
 * @property {Boolean} autoTrim
 * @property {Boolean} forceValueSetting
 * @property {Number} zIndex
 * @property {Number} flexShrink
 * @property {Number} flexGrow
 * @property {Number | String} flexBasis
 * @property {"nowrap" | "wrap" | "wrap-reverse"} flexWrap
 * @property {Number} opacity
 * @property {Boolean} colorTransition
 * @property {Boolean} sizeTransition
 * @property {Boolean} positionTransition
 * @property {Boolean} allTransition
 * @property {Number} transitionDuration
 * @property {Boolean} noBorder
 * @property {Boolean} noDisplay
 * @property {String} className
 * @property {String} caretColor
 * @property {Boolean} fieldSizing
 * @property {Boolean} autoFocus
 * @property {(callbacks) => void} onMounted
 * @property {String | Number} lineHeight
 * @property {React.Ref} ctrls
 * 
 * 
 * @param {xinputparams & xinputstyleparams} param0
 * @returns 
 */
function XInput({
  style, 
  width = "100%",
  height = "auto",
  minWidth = undefined,
  maxWidth = undefined,
  minHeight = undefined,
  maxHeight = undefined, 
  p = 5,
  pH = undefined,
  pV = undefined,
  pT = undefined,
  pB = undefined,
  pL = undefined,
  pR = undefined,
  m = undefined,
  mH = undefined,
  mV = undefined,
  mT = undefined,
  mB = undefined,
  mL = undefined,
  mR = undefined,
  round = 0,
  bW = 1,
  bStyle = "solid",
  color = undefined,
  bColor = undefined,
  bgColor = undefined,
  focus_bW = undefined,
  focus_bStyle = "solid",
  focus_color = undefined,
  focus_bColor = undefined,
  focus_bgColor = undefined,
  error_bW = undefined,
  error_bStyle = "solid",
  error_color = undefined,
  error_bColor = ColorX.CSS("Error"),
  error_bgColor = undefined,
  disabled_bW = undefined,
  disabled_bStyle = "solid",
  disabled_color = ColorX.CSS("greyOut"),
  disabled_bColor = undefined,
  disabled_bgColor = undefined,
  placeholderColor = ColorX.CSS("greyOut", 0.5),
  shadow = undefined,
  shadowSize = 1,
  shadowColor = ColorX.CSS("greyOut", 0.2),
  forwardRef = null, 
  value, 
  onChangeText,
  onChange = undefined,
  placeholder = "",
  boxed = false,
  type = "text",
  numberOfLines = 1,
  onKeyDown = null,
  onArrowKeyDown = null,
  onEnterKeyDown = null,
  onDeleteKeyDown = null,
  onPaste = null,
  readOnly = false,
  error = false,
  textAlign = undefined,
  fontSize = undefined,
  fontFamily = undefined,
  onFocus = null,
  onBlur = null,
  resizable = false,
  multilines = false,
  noSpinners = false,
  accept = undefined,
  jumpCursor = false,
  isControlled = false,
  autoComplete = undefined,
  autoSetCursor = false,
  min = undefined,
  max = undefined,
  step = undefined,
  presicion = undefined,
  multiple = undefined,
  maxLength = undefined,
  noSpecialCharacter = false,
  numberOnly = false,
  emailOnly = false,
  noSpace = false,
  autoTrim = false,
  forceValueSetting = false,
  zIndex = undefined,
  flexShrink = undefined,
  flexGrow = undefined,
  flexWrap = undefined,
  flexBasis = undefined,
  opacity = undefined,
  colorTransition = false,
  sizeTransition = false,
  positionTransition = false,
  allTransition = false,
  transitionDuration = 500,
  noBorder = false,
  fullWidth = undefined,
  fullHeight = undefined,
  autoWidth = undefined,
  autoHeight = undefined,
  fitWidthToContent = undefined,
  fitHeightToContent = undefined,
  lockWidth = undefined,
  lockHeight = undefined,
  noDisplay = false,
  className = undefined,
  caretColor = undefined,
  fieldSizing = undefined,
  autoFocus = false,
  onMounted = null,
  lineHeight = undefined,
  ctrls = undefined,
  ...others
}){

  const [focus, setFocus] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [_thisvalue, setThisValue] = useState(isControlled ? "" : undefined);
  const [inputing, setInputing] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
  
    let callbacks = {
      Focus: () => ref.current?.focus()
    };
  
    onMounted?.(callbacks);
  }, [onMounted]);

  const IsDirty = useCallback((_newValue) => {
    return _newValue !== _thisvalue;
  }, [_thisvalue]);

  useEffect(() => {
    if(isControlled && IsDirty(value)){
      setThisValue(value);
      forceSetValue();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, IsDirty, value]);

  useEffect(() => {
    const input = ref?.current;
    if (input && type === "text") input.setSelectionRange(cursor, cursor);
  }, [ref, cursor, type]);

  const _onInputChange = (e) => {
    if(e.type !== "compositionend" && inputing) return;
    if(autoSetCursor) setCursor(e.target.selectionStart + 1);
    let value = e.target.value;
    if(noSpecialCharacter){
      value = value.replace(/[^a-z0-9]/gi, "");
      setThisValue(value);
      forceSetValue();
    }
    if(numberOnly){
      value = value.replace(/[^0-9]/gi, "");
      setThisValue(value);
      forceSetValue();
    }
    if(emailOnly){
      value = value.replace(/[^a-z0-9@._-]/gi, "");
      setThisValue(value);
      forceSetValue();
    }
    if(noSpace){
      value = value.replace(/ /gi, "");
      setThisValue(value);
      forceSetValue();
    }
    if(autoTrim){
      value = value.trim();
      setThisValue(value);
      forceSetValue();
    }
    onChange ? onChange?.(e, value) : onChangeText?.(value);
  };

  useImperativeHandle(ctrls, () => ({
    Focus: () => ref.current?.focus()
  }), []);

  //Quick Access
  let _addStyle = {};
  if(width) _addStyle.width = width;
  if(height) _addStyle.height = height;
  
  if(p) _addStyle.padding = p;
  if(pH) _addStyle.paddingLeft = _addStyle.paddingRight = pH;
  if(pV) _addStyle.paddingTop = _addStyle.paddingBottom = pV;
  if(pT) _addStyle.paddingTop = pT;
  if(pB) _addStyle.paddingBottom = pB;
  if(pL) _addStyle.paddingLeft = pL;
  if(pR) _addStyle.paddingRight = pR;
  if(m) _addStyle.margin = m;
  if(mH) _addStyle.marginLeft = _addStyle.marginRight = mH;
  if(mV) _addStyle.marginTop = _addStyle.marginBottom = mV;
  if(mT) _addStyle.marginTop = mT;
  if(mB) _addStyle.marginBottom = mB;
  if(mL) _addStyle.marginLeft = mL;
  if(mR) _addStyle.marginRight = mR;
  
  if(round !== undefined) {
    if(_.isArray(round)){
      _addStyle.borderTopLeftRadius = round[0] ?? 0;
      _addStyle.borderTopRightRadius = round[1] ?? 0;
      _addStyle.borderBottomLeftRadius = round[2] ?? 0;
      _addStyle.borderBottomRightRadius = round[3] ?? 0;
    }else{
      _addStyle.borderRadius = round;
    }
  }

  if(shadow) _addStyle.boxShadow = `1px 1px ${shadowSize}px ${shadowSize}px ${shadowColor}`;
  if(fullWidth) _addStyle.width = "100%";
  if(fullHeight) _addStyle.height = "100%";
  if(fitWidthToContent) _addStyle.width = "fit-content";
  if(fitHeightToContent) _addStyle.height = "fit-content";
  if(autoWidth) _addStyle.width = "auto";
  if(autoHeight) _addStyle.height = "auto";

  if(bW) {
    _addStyle.borderWidth = bW;
    if(bW > 0) _addStyle.borderStyle = bStyle;
  }
  if(bgColor) _addStyle.backgroundColor = bgColor;
  if(bColor) _addStyle.borderColor = bColor;
  if(color) _addStyle.color = color;

  if(focus){
    if(!_.isUndefined(focus_bW)) {
      _addStyle.borderWidth = focus_bW;
      if(focus_bW > 0 && focus_bStyle) _addStyle.borderStyle = focus_bStyle;
    }
    if(focus_bgColor) _addStyle.backgroundColor = focus_bgColor;
    if(focus_bColor) _addStyle.borderColor = focus_bColor;
    if(focus_color) _addStyle.color = focus_color;
  }

  if(error && !readOnly){
    if(!_.isUndefined(error_bW)) {
      _addStyle.borderWidth = error_bW;
      if(error_bW > 0 && error_bStyle) _addStyle.borderStyle = error_bStyle;
    }
    if(error_bgColor) _addStyle.backgroundColor = error_bgColor;
    if(error_bColor) _addStyle.borderColor = error_bColor;
    if(error_color) _addStyle.color = error_color;
  }

  if(readOnly){
    if(!_.isUndefined(disabled_bW)) {
      _addStyle.borderWidth = disabled_bW;
      if(disabled_bW > 0 && disabled_bStyle) _addStyle.borderStyle = disabled_bStyle;
    }
    _addStyle.backgroundColor = disabled_bgColor ?? bgColor ?? ColorX.CSS("bgGreyOut");
    if(disabled_bColor) _addStyle.borderColor = disabled_bColor;
    if(disabled_color) _addStyle.color = disabled_color;
  }

  if(textAlign){
    switch(textAlign){
      case "[": _addStyle.textAlign = "left"; break;
      case "|": _addStyle.textAlign = "center"; break;
      case "]": _addStyle.textAlign = "right"; break;
      default: break;
    }
  }

  if(fontSize){
    _addStyle.fontSize = fontSize;
  }

  if(fontFamily){
    _addStyle.fontFamily = fontFamily;
  }

  if(placeholderColor){
    _addStyle["::placeholder"] = placeholderColor;
  }

  if(fieldSizing){
    _addStyle.fieldSizing = "content";
  }

  if(zIndex !== undefined) _addStyle.zIndex = zIndex;
  if(flexShrink !== undefined) _addStyle.flexShrink = flexShrink;
  if(flexGrow) _addStyle.flexGrow = flexGrow;
  if(flexWrap !== undefined) _addStyle.flexWrap = flexWrap;
  if(flexBasis !== undefined) _addStyle.flexBasis = flexBasis;
  if(opacity !== undefined) _addStyle.opacity = opacity;
  if(minWidth !== undefined) _addStyle.minWidth = minWidth;
  if(maxWidth !== undefined) _addStyle.maxWidth = maxWidth;
  if(minHeight !== undefined) _addStyle.minHeight = minHeight;
  if(maxHeight !== undefined) _addStyle.maxHeight = maxHeight;
  if(caretColor !== undefined) _addStyle.caretColor = caretColor;
  if(lineHeight !== undefined) _addStyle.lineHeight = lineHeight;

  if(lockHeight !== undefined){
    _addStyle.height = lockHeight;
    _addStyle.minHeight = lockHeight;
    _addStyle.maxHeight = lockHeight;
  }

  if(lockWidth !== undefined){
    _addStyle.width = lockWidth;
    _addStyle.minWidth = lockWidth;
    _addStyle.maxWidth = lockWidth;
  }

  let transition = style?.transition?.split(",") ?? [];
  if(colorTransition){
    transition.push(`background-color ${transitionDuration}ms`);
    transition.push(`color ${transitionDuration}ms`);
    transition.push(`border-color ${transitionDuration}ms`);
    transition.push(`opacity ${transitionDuration}ms`);
  }else{
    if(color?.day && color?.night){
      transition.push(`color ${transitionDuration}ms`);
    }
    if(bgColor?.day && bgColor?.night){
      transition.push(`background-color ${transitionDuration}ms`);
    }
    if(bColor?.day && bColor?.night){
      transition.push(`border-color ${transitionDuration}ms`);
    }
  }
  
  if(positionTransition){
    transition.push(`top ${transitionDuration}ms`);
    transition.push(`left ${transitionDuration}ms`);
    transition.push(`right ${transitionDuration}ms`);
    transition.push(`bottom ${transitionDuration}ms`);
  }
  if(sizeTransition){
    transition.push(`width ${transitionDuration}ms`);
    transition.push(`height ${transitionDuration}ms`);
    transition.push(`min-width ${transitionDuration}ms`);
    transition.push(`min-height ${transitionDuration}ms`);
    transition.push(`max-width ${transitionDuration}ms`);
    transition.push(`max-height ${transitionDuration}ms`);
  }
  if(allTransition){
    transition.push(`all ${transitionDuration}ms`);
  }
  if(transition?.length > 0){
    _addStyle.transition = transition.join(", ");
  }

  if(noBorder){
    _addStyle.borderWidth = 0;
    _addStyle.outline = "none !important";
  }

  if(noDisplay){
    _addStyle.display = "none";
  }

  let tempClass = [];
  if(noSpinners){
    tempClass.push("no-spinners");
  }
  if(className){
    tempClass.push(className);
  }

  /**
   * 
   * @param {import("react").KeyboardEvent} e 
   */
  const _onKeyDown = (e) => {
    if(!e.shiftKey && e.key === "Enter"){
      onEnterKeyDown?.(e.target.value, e);
    }
    if(!e.shiftKey && (e.key === "Backspace" || e.key === "Delete")){
      onDeleteKeyDown?.(e.target.value, e);
    }
    if(!e.shiftKey && LogicX.isOneOf(e.key, ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"])){
      onArrowKeyDown?.(e.target.value, e.key, e);
    }
    onKeyDown?.(e.target.value, e);
  };

  const _onFocus = (e) => {
    onFocus?.(e);
    setFocus(true);
  };

  const _onBlur = (e) => {
    onBlur?.(e);
    setFocus(false);
  };

  const forceSetValue = () => {
    if (ref.current) {
      const input = ref.current;
      input.value = value ?? "";
      input.setAttribute("value", value ?? "");
    }
  };

  others = XProtect.NativeProps(others);

  if(multilines || numberOfLines > 1){
    _addStyle.resize = resizable ? "both" : "none";
    return(
      <textarea
        className={tempClass?.join(" ") ?? undefined}
        ref={forwardRef || ref}
        type={type}
        style={{
          minWidth: 0,
          ..._addStyle,
          ...style
        }} 
        onChange={_onInputChange} 
        onKeyDown={_onKeyDown}
        onFocus={_onFocus}
        onBlur={_onBlur}
        defaultValue={isControlled ? undefined : value}
        value={forceValueSetting ? (value ?? "") : undefined}
        placeholder={LocaleX.Parse(placeholder)}
        disabled={readOnly}
        rows={numberOfLines}
        autoComplete={autoComplete}
        onCompositionStart={() => setInputing(true)}
        onCompositionEnd={(e) => {
          setInputing(false);
          _onInputChange(e);
        }}
        maxLength={maxLength}
        onPaste={onPaste}
        autoFocus={autoFocus}
        {...others}
        />
    );
  }

  return (
    <input 
      autoFocus={autoFocus}
      defaultValue={isControlled ? undefined : value}
      value={forceValueSetting ? (value ?? "") : undefined}
      className={tempClass?.join(" ") ?? undefined}
      ref={forwardRef || ref}
      type={type}
      style={{
        minWidth: 0,
        ..._addStyle,
        ...style
      }} 
      accept={accept}
      onChange={_onInputChange} 
      onKeyDown={_onKeyDown}
      onFocus={_onFocus}
      onBlur={_onBlur}
      placeholder={LocaleX.Parse(placeholder)}
      disabled={readOnly}
      autoComplete={autoComplete}
      onCompositionStart={() => setInputing(true)}
      onCompositionEnd={(e) => {
        setInputing(false);
        _onInputChange(e);
      }}
      min={min}
      max={max}
      step={step}
      presicion={presicion}
      multiple={multiple}
      maxLength={maxLength}
      onPaste={onPaste}
      {...others}/>
  );
}

export default XInput;
